/**
 * Catalog (P27 + P30 strip).
 *
 * Reads `GET /api/v1/catalog/servers`. Renders one card per active server.
 * Wireframe fixture fallback removed — if the API is unreachable the page
 * shows a clear error; if it returns no servers it shows an empty state
 * with the curl that registers one.
 */
import { useState, type JSX } from 'react';
import { ArrowRight, PlugZap, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  useCatalogServers,
  useServerHealthAll,
  type McpServerRow,
  type ServerHealthSnapshot,
} from '@/api/index.js';
import { ActionButton } from '@/components/console/ActionButton.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { StatusPill, statusToneFrom } from '@/components/console/StatusPill.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';
import { cn } from '@/lib/utils.js';

interface CatalogCard {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly owner: string;
  readonly category: string;
  readonly status: string;
  readonly auth: string;
  readonly class: string;
  readonly risk: string;
  readonly adapter: string;
  readonly color: 'teal' | 'blue' | 'amber' | 'red' | 'green' | 'purple';
}

const COLOR_PALETTE: CatalogCard['color'][] = ['teal', 'blue', 'amber', 'red', 'green', 'purple'];

function serverCard(s: McpServerRow, idx: number): CatalogCard {
  const riskTier = s.riskTier ?? 'medium';
  return {
    id: s.id,
    slug: s.slug,
    name: s.name,
    owner: s.owningWgId,
    category: s.environment ?? 'Production',
    status:
      s.status === 'active'
        ? 'Approved'
        : s.status === 'in_review'
          ? 'Review'
          : s.status === 'retired' || s.status === 'deprecated'
            ? 'Blocked'
            : 'Pilot',
    auth: s.backendType,
    class: riskTier === 'critical' ? 'PHI' : riskTier === 'high' ? 'Confidential' : 'Moderate',
    risk:
      riskTier === 'critical'
        ? 'Critical'
        : riskTier === 'high'
          ? 'High'
          : riskTier === 'medium'
            ? 'Medium'
            : 'Low',
    adapter:
      s.backendType === 'native_mcp'
        ? 'Native MCP'
        : s.backendType === 'rest_wrapper'
          ? 'REST adapter'
          : 'Custom adapter',
    color: COLOR_PALETTE[idx % COLOR_PALETTE.length] ?? 'teal',
  };
}

export default function Catalog(): JSX.Element {
  const { data, isLoading, isError, error } = useCatalogServers();
  const cards: CatalogCard[] = (data?.items ?? []).map(serverCard);
  const health = useServerHealthAll();
  const healthMap = new Map<string, ServerHealthSnapshot>();
  for (const h of health.data?.items ?? []) healthMap.set(h.serverId, h);

  // Build category chips from the data we actually have.
  const categories = ['All', ...new Set(cards.map((c) => c.category))];
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const visible = cards.filter((c) => activeCategory === 'All' || c.category === activeCategory);

  return (
    <>
      <PageHeader
        eyebrow="Discover"
        title="Governed Tool Catalog"
        description="Active MCP servers visible to you. Click a card to inspect tools, schemas, and approval state."
      >
        <Link to="/build/register">
          <ActionButton icon={Plus} primary>
            Register MCP server
          </ActionButton>
        </Link>
      </PageHeader>

      <AsyncShell
        isLoading={isLoading}
        isError={isError}
        isEmpty={cards.length === 0}
        error={error}
        loadingTitle="Loading catalog"
        errorTitle="Catalog API unreachable"
        errorHint={
          <>
            The control-plane API is unreachable. Try a hard refresh (Ctrl+Shift+R) — if it
            persists, the gateway service may be down.
          </>
        }
        emptyTitle="No active servers in the registry"
        emptyHint={
          <>
            Register one in <Link to="/build/register">Register Server</Link>.
          </>
        }
      >
        <p className="subtle" style={{ fontSize: 14, marginBottom: 12 }}>
          Showing {cards.length} active server{cards.length === 1 ? '' : 's'}.
        </p>

        {categories.length > 1 && (
          <div className="chip-row" role="tablist" aria-label="Server category">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                role="tab"
                aria-selected={category === activeCategory}
                className={cn(category === activeCategory && 'active')}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        <div className="toolkit-grid">
          {visible.map((card) => (
            <ToolkitCardView key={card.slug} card={card} health={healthMap.get(card.id)} />
          ))}
        </div>
      </AsyncShell>
    </>
  );
}

function ToolkitCardView({
  card,
  health,
}: {
  card: CatalogCard;
  health?: ServerHealthSnapshot;
}): JSX.Element {
  const dotColor =
    health?.status === 'up' ? '#2f855a' : health?.status === 'down' ? '#c0392b' : '#999';
  const dotTitle = health
    ? `${health.status}${health.latencyMs !== undefined ? ` · ${health.latencyMs}ms` : ''}${health.error ? ` · ${health.error}` : ''} (checked ${new Date(health.checkedAt).toLocaleTimeString()})`
    : 'health probe pending';
  return (
    <Link
      to={`/catalog/toolkits/${card.slug}`}
      className="toolkit-card"
      aria-label={`${card.name} — ${card.owner}`}
      style={{ color: 'var(--foreground)', textDecoration: 'none' }}
    >
      <div className="toolkit-card-top">
        <div className={`toolkit-mark ${card.color}`} aria-hidden>
          <PlugZap size={18} />
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span
            title={dotTitle}
            style={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: dotColor,
              display: 'inline-block',
            }}
          />
          <StatusPill label={card.status} tone={statusToneFrom(card.status)} />
        </div>
      </div>
      <div className="toolkit-main">
        <h3>{card.name}</h3>
        <p>
          {card.owner} · {card.category}
        </p>
      </div>
      <div className="toolkit-meta">
        <span>{card.auth}</span>
        <span>{card.class}</span>
        <span>{card.risk}</span>
        <span>{card.adapter}</span>
      </div>
      <div className="card-footer-row">
        <span>Click to open detail</span>
        <ArrowRight size={15} aria-hidden />
      </div>
    </Link>
  );
}
